/**
 * SSO Providers Section
 * 
 * Admin UI for managing SSO providers for a tenancy.
 * Allows admins to:
 * - Add SAML/OIDC SSO providers
 * - Configure SSO settings
 * - Enable/disable providers
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { useCurrentUser } from '@/contexts/TenancyContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Plus, 
  Trash2, 
  Edit, 
  Key,
  Shield,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faShieldCheck, faXmark } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  SSOProvider,
  SSOProviderFormData,
  SSOProviderType,
  AuthorisedDomain,
} from '@/types/authorisedDomains';
import {
  getSSOProviders,
  createSSOProvider,
  updateSSOProvider,
  deleteSSOProvider,
  getAuthorisedDomains,
  updateAuthorisedDomain,
} from '@/services/authDomainService';
import { Checkbox } from '@/components/ui/checkbox';

const SSO_TYPE_OPTIONS: { value: SSOProviderType; label: string; description: string }[] = [
  { 
    value: 'saml', 
    label: 'SAML 2.0', 
    description: 'Security Assertion Markup Language - commonly used by enterprise IdPs' 
  },
  { 
    value: 'oidc', 
    label: 'OpenID Connect', 
    description: 'Modern OAuth-based authentication protocol' 
  },
];

export function SSOProvidersSection() {
  const { activeTenancy } = useTenancy();
  const currentUser = useCurrentUser();
  const tenancyId = activeTenancy?.id;

  const [providers, setProviders] = useState<SSOProvider[]>([]);
  const [domains, setDomains] = useState<AuthorisedDomain[]>([]);
  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<SSOProvider | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<SSOProviderFormData>({
    name: '',
    displayName: '',
    type: 'saml',
    enabled: true,
    samlConfig: {
      idpEntityId: '',
      ssoUrl: '',
      x509Certificates: [],
    },
    oidcConfig: {
      issuer: '',
      clientId: '',
      clientSecret: '',
    },
  });

  const loadData = useCallback(async () => {
    if (!tenancyId) return;

    setIsLoading(true);
    try {
      const [providersData, domainsData] = await Promise.all([
        getSSOProviders(tenancyId),
        getAuthorisedDomains(tenancyId),
      ]);
      setProviders(providersData);
      setDomains(domainsData);
    } catch (error) {
      console.error('[SSOProvidersSection] Error loading data:', error);
      toast.error('Failed to load SSO providers');
    } finally {
      setIsLoading(false);
    }
  }, [tenancyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      type: 'saml',
      enabled: true,
      samlConfig: {
        idpEntityId: '',
        ssoUrl: '',
        x509Certificates: [],
      },
      oidcConfig: {
        issuer: '',
        clientId: '',
        clientSecret: '',
      },
    });
    setSelectedDomainIds([]);
  };

  const handleAddProvider = async () => {
    if (!tenancyId || !currentUser?.userID) return;

    if (!formData.name) {
      toast.error('Please enter a provider name');
      return;
    }

    // Validate SAML or OIDC config
    if (formData.type === 'saml') {
      if (!formData.samlConfig?.idpEntityId || !formData.samlConfig?.ssoUrl || !formData.samlConfig?.x509Certificates?.length) {
        toast.error('Please fill in all SAML configuration fields');
        return;
      }
    } else if (formData.type === 'oidc') {
      if (!formData.oidcConfig?.issuer || !formData.oidcConfig?.clientId) {
        toast.error('Please fill in all OIDC configuration fields');
        return;
      }
    }

    setSaving(true);
    try {
      const result = await createSSOProvider(
        tenancyId,
        formData,
        currentUser.userID
      );

      if (result.success && result.provider) {
        // Link selected domains to this provider
        if (selectedDomainIds.length > 0) {
          const linkPromises = selectedDomainIds.map(domainId => {
            const domain = domains.find(d => d.id === domainId);
            if (domain) {
              return updateAuthorisedDomain(tenancyId, domainId, {
                ssoProviderId: result.provider!.id,
                firebaseProviderId: result.provider!.firebaseProviderId,
              }, currentUser.userID);
            }
            return Promise.resolve({ success: true });
          });
          
          await Promise.all(linkPromises);
          toast.success(`SSO provider created and linked to ${selectedDomainIds.length} domain(s)`);
        } else {
          toast.success('SSO provider created successfully');
        }
        
        setProviders(prev => [result.provider!, ...prev]);
        setIsAddDialogOpen(false);
        resetForm();
        loadData(); // Refresh to get updated domain links
      } else {
        toast.error(result.error || 'Failed to add SSO provider');
      }
    } catch (error) {
      console.error('[SSOProvidersSection] Error adding provider:', error);
      toast.error('Failed to add SSO provider');
    } finally {
      setSaving(false);
    }
  };

  const handleEditProvider = async () => {
    if (!tenancyId || !currentUser?.userID || !selectedProvider) return;

    try {
      const result = await updateSSOProvider(
        tenancyId,
        selectedProvider.id,
        formData,
        currentUser.userID
      );

      if (result.success) {
        setProviders(prev => prev.map(p => 
          p.id === selectedProvider.id ? { ...p, ...formData } : p
        ));
        setIsEditDialogOpen(false);
        setSelectedProvider(null);
        resetForm();
        toast.success('SSO provider updated successfully');
      } else {
        toast.error(result.error || 'Failed to update SSO provider');
      }
    } catch (error) {
      console.error('[SSOProvidersSection] Error updating provider:', error);
      toast.error('Failed to update SSO provider');
    }
  };

  const handleDeleteProvider = async (provider: SSOProvider) => {
    if (!tenancyId) return;

    if (!confirm(`Are you sure you want to delete "${provider.displayName || provider.name}"? This may affect users who sign in with this provider.`)) {
      return;
    }

    try {
      const result = await deleteSSOProvider(tenancyId, provider.id);
      
      if (result.success) {
        setProviders(prev => prev.filter(p => p.id !== provider.id));
        toast.success('SSO provider deleted');
      } else {
        toast.error(result.error || 'Failed to delete SSO provider');
      }
    } catch (error) {
      console.error('[SSOProvidersSection] Error deleting provider:', error);
      toast.error('Failed to delete SSO provider');
    }
  };

  const handleToggleEnabled = async (provider: SSOProvider) => {
    if (!tenancyId || !currentUser?.userID) return;

    try {
      const result = await updateSSOProvider(
        tenancyId,
        provider.id,
        { enabled: !provider.enabled },
        currentUser.userID
      );

      if (result.success) {
        setProviders(prev => prev.map(p => 
          p.id === provider.id ? { ...p, enabled: !p.enabled } : p
        ));
        toast.success(`SSO provider ${!provider.enabled ? 'enabled' : 'disabled'}`);
      } else {
        toast.error(result.error || 'Failed to update SSO provider');
      }
    } catch (error) {
      console.error('[SSOProvidersSection] Error toggling provider:', error);
      toast.error('Failed to update SSO provider');
    }
  };

  const openEditDialog = (provider: SSOProvider) => {
    setSelectedProvider(provider);
    setFormData({
      name: provider.name,
      displayName: provider.displayName || '',
      type: provider.type,
      firebaseProviderId: provider.firebaseProviderId || '',
      enabled: provider.enabled,
    });
    setIsEditDialogOpen(true);
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success('Copied to clipboard');
  };

  // Generate callback URLs for the provider
  const getCallbackUrls = () => {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isHybrid = window.location.hostname === 'hybrid.deit.dev';
    const isUAT = window.location.hostname === 'admin.deit.dev';
    const isProd = ["my.deiterate.com", "portal.deiterate.com", "admin.deiterate.com", "login.deiterate.com"].includes(window.location.hostname);
    const baseUrl = isDev ? `https://deiterate-hybrid.firebaseapp.com` 
                    : isHybrid ? `https://deiterate-hybrid.firebaseapp.com` 
                    : isUAT ? `https://dev-deiterate.firebaseapp.com`
                    : isProd ? `https://auth.deiterate.com`
                    : window.location.origin;
    return {
      acsUrl: `${baseUrl}/__/auth/handler`,
      entityId: `${window.location.origin}/`,
      metadataUrl: `${baseUrl}/auth/sso/metadata.xml`,
    };
  };

  const callbackUrls = getCallbackUrls();

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-linear-to-br from-amber-500/10 to-amber-500/30 flex items-center justify-center">
                <FontAwesomeIcon icon={faKey} className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <CardTitle>SSO Providers</CardTitle>
                <CardDescription>
                  Configure Single Sign-On providers for your organization
                </CardDescription>
              </div>
            </div>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Provider
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading SSO providers...
            </div>
          ) : providers.length === 0 ? (
            <div className="text-center py-12 border border-dashed rounded-lg">
              <Key className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No SSO Providers</h3>
              <p className="text-muted-foreground mb-4">
                Add an SSO provider to enable single sign-on for your organization
              </p>
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Provider
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.map((provider) => (
                  <TableRow key={provider.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-linear-to-br from-amber-500/10 to-amber-500/30 flex items-center justify-center">
                          <Key className="w-4 h-4 text-amber-500" />
                        </div>
                        <div>
                          <p className="font-medium">{provider.displayName || provider.name}</p>
                          <p className="text-xs text-muted-foreground">{provider.name}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{provider.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={provider.enabled}
                          onCheckedChange={() => handleToggleEnabled(provider)}
                        />
                        <span className={provider.enabled ? 'text-green-600' : 'text-muted-foreground'}>
                          {provider.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(provider)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteProvider(provider)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Service Provider Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-blue-500/10 to-blue-500/30 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <CardTitle>Service Provider Details</CardTitle>
              <CardDescription>
                Use these values when configuring your Identity Provider (IdP)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <Label className="text-xs text-muted-foreground">ACS URL / Callback URL</Label>
                <p className="font-mono text-sm">{callbackUrls.acsUrl}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(callbackUrls.acsUrl, 'acsUrl')}
              >
                {copiedField === 'acsUrl' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <Label className="text-xs text-muted-foreground">Entity ID / Audience URI</Label>
                <p className="font-mono text-sm">{callbackUrls.entityId}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(callbackUrls.entityId, 'entityId')}
              >
                {copiedField === 'entityId' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Provider Dialog - Styled like AddPolicyDialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={(open) => { if (!saving) { setIsAddDialogOpen(open); if (!open) resetForm(); } }}>
        <DialogContent 
          className="max-w-4xl! w-4xl! max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={() => { if (!saving) { setIsAddDialogOpen(false); resetForm(); } }}
              disabled={saving}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Header */}
            <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-amber-500/10 to-amber-500/30 mb-4">
                <FontAwesomeIcon icon={faKey} className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">Add SSO Provider</h2>
              <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                Configure a {formData.type === 'saml' ? 'SAML 2.0' : 'OpenID Connect'} provider for enterprise single sign-on.
              </p>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1 overflow-auto">
              <div className="p-8 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Provider Details</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Provider ID *</Label>
                      <Input
                        id="name"
                        placeholder="e.g., okta, azure-ad"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                        disabled={saving}
                      />
                      <p className="text-xs text-muted-foreground">Unique identifier (lowercase)</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="displayName">Display Name</Label>
                      <Input
                        id="displayName"
                        placeholder="e.g., Okta SSO"
                        value={formData.displayName}
                        onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                        disabled={saving}
                      />
                      <p className="text-xs text-muted-foreground">Shown to users on login</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Provider Type *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value: SSOProviderType) => setFormData(prev => ({ ...prev, type: value }))}
                      disabled={saving}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SSO_TYPE_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex flex-col">
                              <span>{option.label}</span>
                              <span className="text-xs text-muted-foreground">{option.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* SAML Configuration */}
                {formData.type === 'saml' && (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Shield className="w-5 h-5 text-amber-500" />
                      SAML 2.0 Configuration
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Get these values from your Identity Provider (Okta, Azure AD, etc.)
                    </p>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="idpEntityId">IdP Entity ID *</Label>
                        <Input
                          id="idpEntityId"
                          placeholder="e.g., http://www.okta.com/exk1234567890"
                          value={formData.samlConfig?.idpEntityId || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            samlConfig: { ...prev.samlConfig!, idpEntityId: e.target.value }
                          }))}
                          disabled={saving}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="ssoUrl">SSO URL (Login URL) *</Label>
                        <Input
                          id="ssoUrl"
                          placeholder="e.g., https://company.okta.com/app/example/sso/saml"
                          value={formData.samlConfig?.ssoUrl || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            samlConfig: { ...prev.samlConfig!, ssoUrl: e.target.value }
                          }))}
                          disabled={saving}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="x509Certificates">X.509 Certificate *</Label>
                        <Textarea
                          id="x509Certificates"
                          placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDpDCCAoygAwIBAgIGAX...&#10;-----END CERTIFICATE-----"
                          value={formData.samlConfig?.x509Certificates?.[0] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            samlConfig: { ...prev.samlConfig!, x509Certificates: e.target.value ? [e.target.value] : [] }
                          }))}
                          className="font-mono text-xs min-h-[120px]"
                          disabled={saving}
                        />
                        <p className="text-xs text-muted-foreground">
                          Paste the full certificate including BEGIN/END markers
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* OIDC Configuration */}
                {formData.type === 'oidc' && (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Key className="w-5 h-5 text-blue-500" />
                      OpenID Connect Configuration
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Get these values from your Identity Provider's OIDC app configuration.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="issuer">Issuer URL *</Label>
                        <Input
                          id="issuer"
                          placeholder="e.g., https://company.okta.com"
                          value={formData.oidcConfig?.issuer || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            oidcConfig: { ...prev.oidcConfig!, issuer: e.target.value }
                          }))}
                          disabled={saving}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="clientId">Client ID *</Label>
                        <Input
                          id="clientId"
                          placeholder="e.g., 0oa1234567890abcdef"
                          value={formData.oidcConfig?.clientId || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            oidcConfig: { ...prev.oidcConfig!, clientId: e.target.value }
                          }))}
                          disabled={saving}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="clientSecret">Client Secret</Label>
                        <Input
                          id="clientSecret"
                          type="password"
                          placeholder="Enter client secret"
                          value={formData.oidcConfig?.clientSecret || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            oidcConfig: { ...prev.oidcConfig!, clientSecret: e.target.value }
                          }))}
                          disabled={saving}
                        />
                        <p className="text-xs text-muted-foreground">
                          Required for authorization code flow
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Domain Selector */}
                {domains.length > 0 && (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <ExternalLink className="w-5 h-5 text-green-500" />
                      Link to Domains
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Select which domains should use this SSO provider for authentication.
                    </p>
                    
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {domains.map(domain => (
                        <label 
                          key={domain.id} 
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                        >
                          <Checkbox
                            checked={selectedDomainIds.includes(domain.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedDomainIds(prev => [...prev, domain.id]);
                              } else {
                                setSelectedDomainIds(prev => prev.filter(id => id !== domain.id));
                              }
                            }}
                            disabled={saving}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{domain.domain}</p>
                            {domain.ssoProviderId && (
                              <p className="text-xs text-amber-500">
                                Already linked to another provider
                              </p>
                            )}
                          </div>
                          {domain.verified && (
                            <Badge variant="outline" className="text-green-500 border-green-500/30">
                              Verified
                            </Badge>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Footer */}
            <div className="p-6 border-t border-white/10 bg-card/50 flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => { setIsAddDialogOpen(false); resetForm(); }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={handleAddProvider} disabled={saving || !formData.name}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Provider'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Provider Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle>Edit SSO Provider</DialogTitle>
            <DialogDescription>
              Update the SSO provider configuration
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-displayName">Display Name</Label>
              <Input
                id="edit-displayName"
                placeholder="e.g., Okta, Azure AD, Google Workspace"
                value={formData.displayName}
                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-firebaseProviderId">Firebase Provider ID</Label>
              <Input
                id="edit-firebaseProviderId"
                placeholder="e.g., saml.okta-provider or oidc.azure-ad"
                value={formData.firebaseProviderId}
                onChange={(e) => setFormData(prev => ({ ...prev, firebaseProviderId: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                The Firebase Auth provider ID configured in Firebase Console
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsEditDialogOpen(false); setSelectedProvider(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleEditProvider}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SSOProvidersSection;
